import { Subscription, User } from "../types";

// ID de la membresía requerida para crear comunidad sin pasar por Typeform
// Producción: 5916 | Testing: 11204
const MEMBERSHIP_ID = 5916;
const TYPEFORM_BASE_URL = "https://klouser.typeform.com/to/On2YARH9?typeform-source=home.klouser.app";

/**
 * Verifica si el usuario está suscrito a la membresía específica
 * @param subscriptions Array de suscripciones del usuario
 * @param membershipId ID de la membresía a verificar (por defecto MEMBERSHIP_ID)
 * @returns boolean indicando si el usuario está suscrito a la membresía
 */
export const isSubscribedToMembership = (
  subscriptions: Subscription[],
): boolean => {
  if (!subscriptions || subscriptions.length === 0) return false;

  const commonsSubscriptionsStates = [
    "active",
    "paid",
    "non_renewing",
    "renewal_retrying",
    "pending_invite",
  ];
  const mpSubscriptionsStates = ["waiting_for_gateway", "not_paid", "retrying"];

  return (
    subscriptions
      ?.filter(
        (subscription) =>
          commonsSubscriptionsStates.includes(subscription.status) ||
          mpSubscriptionsStates.includes(subscription.status)
      )
      .filter((subscription) => subscription.group.id === MEMBERSHIP_ID).length > 0
  );
};

/**
 * Genera la URL del Typeform con los parámetros del usuario
 * @param userInformation Información del usuario
 * @returns string con la URL completa del Typeform
 */
export const getTypeformUrl = (userInformation: User): string => {
  const userId = userInformation?.id || "";
  const email = userInformation?.email || "";
  const initialProfile = userInformation?.expert?.username || userInformation?.email?.split("@")[0] || "";

  return `${TYPEFORM_BASE_URL}#user_id=${userId}&email=${email}&initial_profile=${initialProfile}`;
};

/**
 * Obtiene la URL para el botón "Crear mi comunidad" según las condiciones del usuario
 * @param userInformation Información del usuario
 * @param subscriptions Array de suscripciones del usuario
 * @returns string con la URL apropiada según las condiciones
 */
export const getCreateCommunityUrl = (
  userInformation: User,
  subscriptions: Subscription[]
): string => {
  // Si el usuario ya es experto, mantener el flujo normal
  const hasSpecificMembership = isSubscribedToMembership(subscriptions);
  const isExpert = !!userInformation?.expert && !!userInformation?.expert?.id;

  // Si forma parte de la membresía requerida, flujo normal
  if (hasSpecificMembership || isExpert) {
    return "/dashboard/community";
  }
  // Si NO forma parte de la membresía requerida, redirigir a Typeform
  return getTypeformUrl(userInformation);
};

/**
 * Determina si el link debe abrir en nueva pestaña o redirigir internamente
 * @param userInformation Información del usuario
 * @param subscriptions Array de suscripciones del usuario
 * @returns boolean indicando si debe abrir el link externamente
 */
export const shouldOpenExternally = (
  userInformation: User,
  subscriptions: Subscription[]
): boolean => {
  if (userInformation?.expert) return false;

  const hasSpecificMembership = isSubscribedToMembership(subscriptions);

  // Abrir externamente solo si es suscriptor y NO tiene la membresía requerida
  return !hasSpecificMembership;
};

